import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { Autocomplete } from "@mui/material";
import * as Yup from "yup";
import { toast } from "react-toastify";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { useDispatch } from "react-redux";
//thunk
import { fetchMakes, fetchMakeByDropDown } from "slices/Masters/make/thunk";
import {
  fetchModelById,
  addModel,
  updateModel,
} from "slices/Masters/model/thunk";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { autocompleteDropdownStyles } from "Constants/Style";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const ModelForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [makeOptions, setMakeOptions] = useState<any[]>([]);

  // Load Makes
  useEffect(() => {
    const fetchDropdown = async () => {
      try {
        const res = await dispatch(fetchMakeByDropDown()).unwrap();
        setMakeOptions(res?.data || []);
      } catch {
        toast.error("Failed to load makes");
      }
    };

    const fetchModelData = async () => {
      if (isEdit && id) {
        try {
          setIsLoading(true);
          const data = await dispatch(fetchModelById(id)).unwrap();
          formik.setValues({
            make: data?.make_id || "",
            modelName: data?.model_name || "",
          });
        } catch {
          toast.error("Failed to load model");
          // navigate("/model");
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchDropdown();

    if (isEdit && id) {
      fetchModelData();
    }
  }, [dispatch, id, isEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      make: "",
      modelName: "",
    },

    validationSchema: Yup.object({
      make: Yup.string().required("Please select a make"),
      modelName: Yup.string().trim().required("Please enter model name"),
    }),
    onSubmit: async (values) => {
      const payload = {
        make_id: values.make,
        model_name: values.modelName.trim(),
      };

      setIsSubmitting(true);
      try {
        if (isEdit) {
          await dispatch(updateModel({ id, ...payload })).unwrap();
          toast.success("Model updated successfully");
        } else {
          await dispatch(addModel(payload)).unwrap();
          clearTableState(TableKeys.MASTER.MODEL);
          toast.success("Model added successfully");
        }
        navigate("/master/model");
      } catch (error: any) {
        console.error("Error during form submit:", error);
        toast.error(
          error?.message || "Something went wrong while saving model",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Model" : "Add Model"}
        titleLink="/master/model"
        breadcrumbs={[
          { label: "Model", to: "/master/model" },
          { label: id ? "Edit Model" : "Add Model", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />
      {isEdit && isLoading ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                Make <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={makeOptions}
                getOptionLabel={(option: any) => option.make_name || ""}
                value={
                  makeOptions.find(
                    (opt: any) => opt.make_id === formik.values.make,
                  ) || null
                }
                isOptionEqualToValue={(option: any, value: any) =>
                  option.make_id === value.make_id
                }
                onChange={(_, selected) => {
                  formik.setFieldValue("make", selected?.make_id || "");
                }}
                onBlur={formik.handleBlur}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) =>
                      typeof opt === "string" ? opt : String(opt.make_name),
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select Make"
                    name="make"
                    error={Boolean(formik.touched.make && formik.errors.make)}
                    helperText={
                      formik.touched.make &&
                      typeof formik.errors.make === "string"
                        ? formik.errors.make
                        : ""
                    }
                    fullWidth
                    size="small"
                    sx={{ ...autocompleteDropdownStyles.inputSx }}
                  />
                )}
              />
            </Col>

            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                Model Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="modelName"
                placeholder="Enter Model name"
                value={formik.values.modelName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.modelName && formik.touched.modelName
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.modelName}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton redirectTo="/master/model" disabled={isSubmitting} />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default ModelForm;
